import React, { useState, useEffect, useRef } from 'react';
import { createProduct, updateProduct, getBrands } from '../services/api';
import './ProductForm.css';

function ProductForm({ product, defaultBrand, onSuccess, onClose }) {
  const isEditing = !!product;
  const photoInputRef = useRef(null);
  const materialInputRef = useRef(null);
  
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    brand_id: '',
    model: '',
    usage: '',
    about: '',
  });
  
  // Files and links state
  const [newPhotos, setNewPhotos] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [removePhotos, setRemovePhotos] = useState([]);
  
  const [newMaterials, setNewMaterials] = useState([]);
  const [existingMaterials, setExistingMaterials] = useState([]);
  const [removeMaterials, setRemoveMaterials] = useState([]);
  
  const [links, setLinks] = useState([]);
  
  const [loading, setLoading] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch brands
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await getBrands();
        if (response.success) {
          setBrands(response.brands);
        }
      } catch (err) {
        setError('Failed to load brands');
      } finally {
        setBrandsLoading(false);
      }
    };
    fetchBrands();
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (product) {
      setFormData({
        brand_id: product.brand_id || '',
        model: product.model || '',
        usage: product.usage || '',
        about: product.about || '',
      });
      setExistingPhotos(product.photos || []);
      setExistingMaterials(product.materials || []);
      setLinks(product.links || []);
    } else if (defaultBrand) {
      setFormData(prev => ({
        ...prev,
        brand_id: defaultBrand.id,
      }));
    }
  }, [product, defaultBrand]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  // Handle photo file selection
  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      return validTypes.includes(file.type) && file.size <= 10 * 1024 * 1024;
    });
    
    // Create previews
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewPhotos(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
    
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Remove new photo
  const removeNewPhoto = (index) => {
    setNewPhotos(prev => prev.filter((_, i) => i !== index));
  };

  // Mark existing photo for removal
  const markPhotoForRemoval = (photoPath) => {
    setRemovePhotos(prev => [...prev, photoPath]);
    setExistingPhotos(prev => prev.filter(p => p !== photoPath));
  };

  // Handle material file selection
  const handleMaterialChange = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      const label = file.name.replace(/\.[^/.]+$/, ''); // Remove extension
      setNewMaterials(prev => [...prev, { file, label }]);
    });
    
    if (materialInputRef.current) {
      materialInputRef.current.value = '';
    }
  };

  // Update material label
  const updateMaterialLabel = (index, label) => {
    setNewMaterials(prev => prev.map((m, i) => 
      i === index ? { ...m, label } : m
    ));
  };

  // Remove new material
  const removeNewMaterial = (index) => {
    setNewMaterials(prev => prev.filter((_, i) => i !== index));
  };

  // Mark existing material for removal
  const markMaterialForRemoval = (filePath) => {
    setRemoveMaterials(prev => [...prev, filePath]);
    setExistingMaterials(prev => prev.filter(m => m.file_path !== filePath));
  };

  // Add link
  const addLink = () => {
    setLinks(prev => [...prev, { label: '', url: '' }]);
  };

  // Update link
  const updateLink = (index, field, value) => {
    setLinks(prev => prev.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    ));
  };

  // Remove link
  const removeLink = (index) => {
    setLinks(prev => prev.filter((_, i) => i !== index));
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    if (!formData.brand_id) {
      setError('Please select a brand');
      return;
    }
    
    if (!formData.model.trim()) {
      setError('Model name is required');
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();
      submitData.append('brand_id', formData.brand_id);
      submitData.append('model', formData.model.trim());
      submitData.append('usage', formData.usage.trim());
      submitData.append('about', formData.about.trim());
      
      // Add valid links
      const validLinks = links.filter(l => l.label.trim() && l.url.trim());
      submitData.append('links', JSON.stringify(validLinks));
      
      // Add new photos
      newPhotos.forEach(photo => {
        submitData.append('photos', photo.file);
      });
      
      // Add new materials with labels
      newMaterials.forEach(material => {
        submitData.append('materials', material.file);
        submitData.append('material_labels', material.label);
      });
      
      // For editing, include files to remove
      if (isEditing) {
        submitData.append('remove_photos', JSON.stringify(removePhotos));
        submitData.append('remove_materials', JSON.stringify(removeMaterials));
      }

      let response;
      if (isEditing) {
        response = await updateProduct(product.id, submitData);
      } else {
        response = await createProduct(submitData);
      }

      if (response.success) {
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Failed to save product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="product-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEditing ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="close-btn" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {brandsLoading ? (
          <div className="form-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="product-form">
            {error && (
              <div className="form-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                {error}
              </div>
            )}

            {/* Basic Info Section */}
            <div className="form-section">
              <h3 className="section-title">Basic Information</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="brand_id">Brand *</label>
                  <select
                    id="brand_id"
                    name="brand_id"
                    value={formData.brand_id}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Brand</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="model">Model Name *</label>
                  <input
                    type="text"
                    id="model"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    placeholder="Enter model name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="usage">Usage / Application</label>
                <input
                  type="text"
                  id="usage"
                  name="usage"
                  value={formData.usage}
                  onChange={handleChange}
                  placeholder="e.g., Industrial pumps for oil refineries"
                />
              </div>

              <div className="form-group">
                <label htmlFor="about">About / Description</label>
                <textarea
                  id="about"
                  name="about"
                  value={formData.about}
                  onChange={handleChange}
                  placeholder="Product description..."
                  rows={4}
                />
              </div>
            </div>

            {/* Photos Section */}
            <div className="form-section">
              <h3 className="section-title">Product Photos</h3>
              
              <div className="photos-grid">
                {/* Existing photos */}
                {existingPhotos.map((photo, index) => (
                  <div key={`existing-${index}`} className="photo-item">
                    <img src={photo} alt={`Product ${index + 1}`} />
                    <button 
                      type="button"
                      className="remove-photo"
                      onClick={() => markPhotoForRemoval(photo)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
                
                {/* New photos */}
                {newPhotos.map((photo, index) => (
                  <div key={`new-${index}`} className="photo-item new">
                    <img src={photo.preview} alt={`New ${index + 1}`} />
                    <button 
                      type="button"
                      className="remove-photo"
                      onClick={() => removeNewPhoto(index)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
                
                {/* Add photo button */}
                <div 
                  className="add-photo-btn"
                  onClick={() => photoInputRef.current?.click()}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Add Photo</span>
                </div>
              </div>
              
              <input
                type="file"
                ref={photoInputRef}
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Materials Section */}
            <div className="form-section">
              <h3 className="section-title">Flyers & Documents</h3>
              
              <div className="materials-list">
                {/* Existing materials */}
                {existingMaterials.map((material, index) => (
                  <div key={`existing-${index}`} className="material-item">
                    <div className="material-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <div className="material-info">
                      <span className="material-label">{material.label}</span>
                      <span className="material-type">{material.file_type}</span>
                    </div>
                    <button 
                      type="button"
                      className="remove-material"
                      onClick={() => markMaterialForRemoval(material.file_path)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
                
                {/* New materials */}
                {newMaterials.map((material, index) => (
                  <div key={`new-${index}`} className="material-item new">
                    <div className="material-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="material-label-input"
                      value={material.label}
                      onChange={(e) => updateMaterialLabel(index, e.target.value)}
                      placeholder="Label for this file"
                    />
                    <button 
                      type="button"
                      className="remove-material"
                      onClick={() => removeNewMaterial(index)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              
              <button 
                type="button"
                className="add-material-btn"
                onClick={() => materialInputRef.current?.click()}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Document
              </button>
              
              <input
                type="file"
                ref={materialInputRef}
                accept=".pdf,.ppt,.pptx,.doc,.docx,image/*"
                multiple
                onChange={handleMaterialChange}
                style={{ display: 'none' }}
              />
            </div>

            {/* Links Section */}
            <div className="form-section">
              <h3 className="section-title">External Links</h3>
              
              <div className="links-list">
                {links.map((link, index) => (
                  <div key={index} className="link-item">
                    <input
                      type="text"
                      placeholder="Label (e.g., Datasheet)"
                      value={link.label}
                      onChange={(e) => updateLink(index, 'label', e.target.value)}
                    />
                    <input
                      type="url"
                      placeholder="https://..."
                      value={link.url}
                      onChange={(e) => updateLink(index, 'url', e.target.value)}
                    />
                    <button 
                      type="button"
                      className="remove-link"
                      onClick={() => removeLink(index)}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
              
              <button 
                type="button"
                className="add-link-btn"
                onClick={addLink}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                Add Link
              </button>
            </div>

            <div className="form-actions">
              <button type="button" className="cancel-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <span className="btn-spinner"></span>
                ) : isEditing ? (
                  'Update Product'
                ) : (
                  'Add Product'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ProductForm;
